import React from 'react';

import { useAppSelector } from 'hooks/redux';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { chartOfAccountsSelectors } from 'app-components/flux/chart-of-accounts';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import type { CategorySourceConditionsType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  row: {
    display: 'flex',
    flex: 1,
    flexWrap: 'wrap',
    '> *': {
      lineHeight: '24px',
    },
    '>div': {
      borderRight: `1px ${theme.palette.divider} solid`,
      padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
    },
    alignContent: 'flex-start',
    alignItems: 'center',
  },
  textPaddingAfter: {
    paddingRight: theme.spacing(0.5),
  },
  highlightText: {
    color: theme.palette.info.main,
  },
}));

const CategoryTarget: React.FC<{
  coaTarget?: CategorySourceConditionsType;
  businessCoaTarget?: CategorySourceConditionsType;
  useDefaultAccount: boolean;
}> = ({ coaTarget, businessCoaTarget, useDefaultAccount }) => {
  const { cx, classes } = useStyles();

  const formatCategory = (category: string | undefined) => {
    return category ? category.replace(/:(?!\s)/, ': ') : category;
  };

  const category = coaTarget && chartOfAccountsSelectors.getCoaStringSelector(undefined, coaTarget);
  const businessCategory =
    businessCoaTarget && chartOfAccountsSelectors.getCoaStringSelector(undefined, businessCoaTarget);
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const formattedCategory = formatCategory(category);
  const formattedBusinessCategory = formatCategory(businessCategory);
  /* Check for user downgrade case (QBP to Simplifi)
   * If such user has business category in rule, continue to show business category
   */
  const isDowngraded = businessCategory && !businessEnabled ? true : false;

  return (
    <>
      {category && (
        <Box className={classes.row}>
          <Typography className={classes.textPaddingAfter} variant='bodyRegular'>
            Update Category to
          </Typography>

          {!useDefaultAccount && (!isDowngraded || !businessEnabled) ? (
            <Typography className={cx(classes.textPaddingAfter, classes.highlightText)} variant='bodyMedium'>
              {formattedCategory}
            </Typography>
          ) : (
            <>
              <Typography className={cx(classes.textPaddingAfter, classes.highlightText)} variant='bodyMedium'>
                {formattedBusinessCategory}
              </Typography>
              <Typography className={classes.textPaddingAfter} variant='bodyRegular'>
                in business accounts OR
              </Typography>
              <Typography className={cx(classes.textPaddingAfter, classes.highlightText)} variant='bodyMedium'>
                {category}
              </Typography>
              <Typography className={classes.textPaddingAfter} variant='bodyRegular'>
                in personal accounts
              </Typography>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default CategoryTarget;
